import React from 'react';
import { toast } from 'react-toastify';
import { FiCheck, FiAlertTriangle, FiLoader, FiFileText, FiMic, FiImage, FiFilm } from 'react-icons/fi';
import { useT } from '../../utils/i18n';

export const STEPS = ['script', 'voice', 'images', 'video'];
const STEP_ICONS = { script: FiFileText, voice: FiMic, images: FiImage, video: FiFilm };

/** Words → roughly how long they take to read aloud (Telugu, ElevenLabs v4). */
export const WORDS_PER_SECOND = 1.6;
export const countWords = (text = '') => text.trim().split(/\s+/).filter(Boolean).length;

export const formatSeconds = (sec) => {
  if (sec == null || Number.isNaN(sec)) return '–';
  const s = Math.round(sec);
  return s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`;
};

export const showError = (e, fallback = 'Something went wrong. Please try again.') =>
  toast.error(e?.response?.data?.message || e?.message || fallback);

/** True while any background work on the story is running (the editor polls then). */
export const isBusy = (story) => Boolean(story) && (
  story.script.status === 'generating'
  || story.voice.status === 'generating'
  || story.images.status === 'planning'
  || story.scenes.some((s) => s.status === 'generating')
  || story.video.status === 'rendering'
);

/** How each step stands: 'done' | 'active' | 'busy' | 'failed' | 'outdated' | 'locked'. */
export const stepState = (story, step) => {
  const reached = STEPS.indexOf(step) <= STEPS.indexOf(story.step);
  if (!reached) return 'locked';
  switch (step) {
    case 'script':
      if (story.script.status === 'generating') return 'busy';
      return story.script.approved ? 'done' : 'active';
    case 'voice':
      if (story.voice.status === 'generating') return 'busy';
      if (story.voice.status === 'failed') return 'failed';
      return story.voice.approved ? 'done' : 'active';
    case 'images':
      if (story.images.status === 'planning' || story.scenes.some((s) => s.status === 'generating')) return 'busy';
      if (story.images.status === 'failed') return 'failed';
      return story.images.approved ? 'done' : 'active';
    case 'video':
      if (story.video.status === 'rendering') return 'busy';
      if (story.video.status === 'failed') return 'failed';
      if (story.video.status === 'ready') return story.video.outdated ? 'outdated' : 'done';
      return 'active';
    default:
      return 'locked';
  }
};

// labelPrefix: where the step names come from (Full Story Video names its steps differently).
export const Stepper = ({ story, current, onSelect, labelPrefix = 'fullStory' }) => {
  const t = useT();
  return (
    <ol className="grid grid-cols-4 gap-1.5 sm:gap-3">
      {STEPS.map((step, i) => {
        const state = stepState(story, step);
        const Icon = STEP_ICONS[step];
        const selected = current === step;
        const locked = state === 'locked';
        return (
          <li key={step}>
            <button
              type="button"
              disabled={locked}
              onClick={() => onSelect(step)}
              aria-current={selected ? 'step' : undefined}
              className={`w-full flex items-center gap-2 rounded-xl border px-2.5 py-2 sm:px-3 sm:py-2.5 text-left transition-colors ${
                selected
                  ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-200'
                  : locked
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className={`flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                state === 'done' ? 'bg-green-100 text-green-700'
                  : state === 'failed' ? 'bg-danger-100 text-danger-600'
                    : state === 'outdated' ? 'bg-amber-100 text-amber-700'
                      : selected ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {state === 'done' ? <FiCheck size={14} />
                  : state === 'busy' ? <FiLoader size={13} className="animate-spin" />
                    : state === 'failed' || state === 'outdated' ? <FiAlertTriangle size={13} />
                      : i + 1}
              </span>
              <span className="min-w-0 hidden sm:block">
                <span className="block text-sm font-semibold text-gray-800 truncate">{t(`${labelPrefix}.step.${step}`)}</span>
                <span className="block text-[11px] text-gray-500 truncate">
                  {t(step === 'video' && state === 'done' ? 'fullStory.state.ready' : `fullStory.state.${state}`)}
                </span>
              </span>
              <Icon size={15} className="sm:hidden mx-auto text-gray-500" />
            </button>
          </li>
        );
      })}
    </ol>
  );
};

/** A step's card: title, short description, body, and the footer with the approve button. */
export const StepCard = ({ title, description, children, footer }) => (
  <section className="card space-y-4">
    <header>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
    </header>
    {children}
    {footer && <footer className="pt-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">{footer}</footer>}
  </section>
);

export const Notice = ({ tone = 'info', children }) => {
  const tones = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warn: 'bg-amber-50 border-amber-200 text-amber-800',
    error: 'bg-red-50 border-red-200 text-red-700',
  };
  return <div className={`rounded-xl border px-3.5 py-2.5 text-sm ${tones[tone]}`}>{children}</div>;
};

// How a video clip fills its scene — mirrors clipPlan in the server's
// FullStoryRenderer: a long-enough clip plays at normal speed from its start
// point and is cut at the scene's end; a shorter one is slowed down (to no
// less than 1/1.5 speed) and then holds its last frame for any time left.
export const MAX_CLIP_STRETCH = 1.5;
export const VIDEO_UPLOAD_MAX_MB = 200;
export const clipPlan = (seconds, clipSeconds, clipStart = 0) => {
  const available = Math.max(0, (clipSeconds || 0) - (clipStart || 0));
  if (!clipSeconds || available >= seconds) return { stretch: 1, playedSec: seconds, frozenSec: 0, available };
  const stretch = Math.min(MAX_CLIP_STRETCH, seconds / Math.max(available, 0.01));
  const playedSec = Math.min(seconds, available * stretch);
  return { stretch, playedSec, frozenSec: Math.max(0, seconds - playedSec), available };
};
